import {
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogTitle,
  Portal,
  Root,
} from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import cls from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { Button } from '@/components/atoms/Button/Button';
import { DrawerProps } from '@/components/atoms/Drawer/Drawer';
import { IconCross } from '@/components/Icons/IconCross';
import { H1 } from '../Typography/Typography';
import styles from './Modal.module.scss';

interface Props extends DrawerProps {
  actions: React.ReactNode;
  closeButtonAriaLabel: string;
  isOpen: boolean;
  title: React.ReactNode;
}

export const Modal = ({
  actions,
  ariaLabel = '',
  children,
  className,
  closeButtonAriaLabel,
  isOpen,
  title,
  onDismiss,
  onExitComplete,
  ...rest
}: Props) => (
  <AnimatePresence onExitComplete={onExitComplete} initial>
    {isOpen && (
      <Root open={isOpen} onOpenChange={onDismiss} modal>
        <Portal>
          <DialogOverlay>
            <motion.div
              {...rest}
              className={cls(styles.modal, className)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              exit={{ opacity: 1 }}
            >
              <DialogContent>
                <motion.div
                  data-dialog-content=""
                  initial={{ y: 10, x: '-50%' }}
                  animate={{ y: '-50%' }}
                  exit={{ y: 100, opacity: 0 }}
                  aria-label={ariaLabel}
                  transition={{
                    bounceDamping: 10,
                  }}
                >
                  <VisuallyHidden>
                    <DialogTitle>{ariaLabel}</DialogTitle>
                    <DialogDescription>{ariaLabel}</DialogDescription>
                  </VisuallyHidden>
                  <div className={styles.header}>
                    <H1 version="level4">{title}</H1>
                    <Button
                      onClick={onDismiss}
                      variant="icon"
                      color="default"
                      className={styles.dismissBtn}
                      aria-label={closeButtonAriaLabel}
                    >
                      <IconCross />
                    </Button>
                  </div>
                  <div className={styles.contentWrapper}>
                    <div className={cls(styles.content, 'modalContent')}>
                      {children}
                    </div>
                    <div className="modalContentGradient" />
                  </div>
                  {actions}
                </motion.div>
              </DialogContent>
            </motion.div>
          </DialogOverlay>
        </Portal>
      </Root>
    )}
  </AnimatePresence>
);
